/* ==========================================================================
   Ozeret -- reference data (the shared vocabulary)
   Ported verbatim from the prototype. Replace with API responses --
   the shapes are the schema.
   ========================================================================== */

import type {
  Area, Language, Job, Trait, PayMethod, RatingDim, Weekday, LangLevel,
} from '../types';

export const DOW: Weekday[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const AREAS: Area[] = [
  { id: 'monsey',        county: 'Rockland',  label: 'Monsey' , x: 22, y: 22 },
  { id: 'spring_valley', county: 'Rockland',  label: 'Spring Valley' , x: 21, y: 27 },
  { id: 'new_square',    county: 'Rockland',  label: 'New Square' , x: 18, y: 19 },
  { id: 'suffern',       county: 'Rockland',  label: 'Suffern' , x: 12, y: 26 },
  { id: 'new_city',      county: 'Rockland',  label: 'New City' , x: 33, y: 19 },
  { id: 'nanuet',        county: 'Rockland',  label: 'Nanuet' , x: 28, y: 26 },
  { id: 'pomona',        county: 'Rockland',  label: 'Pomona' , x: 27, y: 14 },
  { id: 'airmont',       county: 'Rockland',  label: 'Airmont' , x: 17, y: 29 },
  { id: 'wesley_hills',  county: 'Rockland',  label: 'Wesley Hills' , x: 23, y: 16 },
  { id: 'chestnut',      county: 'Rockland',  label: 'Chestnut Ridge' , x: 20, y: 32 },
  { id: 'nyack',         county: 'Rockland',  label: 'Nyack' , x: 37, y: 25 },
  { id: 'pearl_river',   county: 'Rockland',  label: 'Pearl River' , x: 27, y: 33 },
  { id: 'haverstraw',    county: 'Rockland',  label: 'Haverstraw' , x: 36, y: 10 },
  { id: 'boro_park',     county: 'Brooklyn',  label: 'Boro Park' , x: 64, y: 70 },
  { id: 'flatbush',      county: 'Brooklyn',  label: 'Flatbush' , x: 67, y: 71 },
  { id: 'midwood',       county: 'Brooklyn',  label: 'Midwood' , x: 68, y: 76 },
  { id: 'williamsburg',  county: 'Brooklyn',  label: 'Williamsburg' , x: 62, y: 58 },
  { id: 'crown_heights', county: 'Brooklyn',  label: 'Crown Heights' , x: 69, y: 65 },
  { id: 'park_slope',    county: 'Brooklyn',  label: 'Park Slope' , x: 62, y: 64 },
  { id: 'bay_ridge',     county: 'Brooklyn',  label: 'Bay Ridge' , x: 55, y: 77 },
  { id: 'bensonhurst',   county: 'Brooklyn',  label: 'Bensonhurst' , x: 60, y: 76 },
  { id: 'sheepshead',    county: 'Brooklyn',  label: 'Sheepshead Bay' , x: 67, y: 84 },
  { id: 'marine_park',   county: 'Brooklyn',  label: 'Marine Park' , x: 73, y: 80 },
  { id: 'canarsie',      county: 'Brooklyn',  label: 'Canarsie' , x: 78, y: 75 },
  { id: 'sunset_park',   county: 'Brooklyn',  label: 'Sunset Park' , x: 59, y: 69 },
  { id: 'greenpoint',    county: 'Brooklyn',  label: 'Greenpoint' , x: 62, y: 54 },
];

export const LANGUAGES: Language[] = [
  { id: 'en', label: 'English' },
  { id: 'es', label: 'Spanish' },
  { id: 'yi', label: 'Yiddish' },
  { id: 'he', label: 'Hebrew' },
  { id: 'ru', label: 'Russian' },
  { id: 'ht', label: 'Haitian Creole' },
  { id: 'pl', label: 'Polish' },
  { id: 'pt', label: 'Portuguese' },
  { id: 'fr', label: 'French' },
  { id: 'uk', label: 'Ukrainian' },
  { id: 'tl', label: 'Tagalog' },
  { id: 'sq', label: 'Albanian' },
];

/** Spoken level matters more than the language itself -- "some English" and
 *  "fluent English" are completely different bookings. */
export const LANG_LEVEL: Record<LangLevel, string> = { basic: 'Basic', good: 'Conversational', fluent: 'Fluent', native: 'Native' };

/** The spine of the product. A home's private list, a home's request, and a
 *  cleaner's specialties all draw from this one list -- which is what makes
 *  filtering and match scoring work at all. */
export const JOB_CATALOG: Job[] = [
  // Kitchen
  { id: 'dishes',        group: 'Kitchen',    label: 'Dishes & sink',            est: 0.5 },
  { id: 'counters',      group: 'Kitchen',    label: 'Counters & backsplash',    est: 0.3 },
  { id: 'stovetop',      group: 'Kitchen',    label: 'Stovetop & range hood',    est: 0.4 },
  { id: 'oven',          group: 'Kitchen',    label: 'Oven interior',            est: 1.0 },
  { id: 'fridge',        group: 'Kitchen',    label: 'Fridge interior',          est: 0.8 },
  { id: 'microwave',     group: 'Kitchen',    label: 'Microwave',                est: 0.2 },
  { id: 'cabinets',      group: 'Kitchen',    label: 'Cabinet fronts',           est: 0.5 },
  { id: 'kosher',        group: 'Kitchen',    label: 'Kosher kitchen (meat / dairy separation)', est: 0.0, skill: true },
  { id: 'pesach',        group: 'Kitchen',    label: 'Pesach cleaning',          est: 4.0, seasonal: true },

  // Bathrooms
  { id: 'toilets',       group: 'Bathrooms',  label: 'Toilets',                  est: 0.3 },
  { id: 'showers',       group: 'Bathrooms',  label: 'Showers & tubs',           est: 0.5 },
  { id: 'bath_sinks',    group: 'Bathrooms',  label: 'Sinks & mirrors',          est: 0.3 },
  { id: 'grout',         group: 'Bathrooms',  label: 'Tile & grout scrub',       est: 1.0 },
  { id: 'restock',       group: 'Bathrooms',  label: 'Restock towels & paper',   est: 0.2 },

  // Floors
  { id: 'vacuum',        group: 'Floors',     label: 'Sweep & vacuum',           est: 0.6 },
  { id: 'mop',           group: 'Floors',     label: 'Mop hard floors',          est: 0.6 },
  { id: 'stairs',        group: 'Floors',     label: 'Stairs',                   est: 0.3 },
  { id: 'carpet_spot',   group: 'Floors',     label: 'Carpet spot clean',        est: 0.4 },

  // Bedrooms & living
  { id: 'linens',        group: 'Bedrooms',   label: 'Change linens',            est: 0.4 },
  { id: 'beds',          group: 'Bedrooms',   label: 'Make beds',                est: 0.2 },
  { id: 'dusting',       group: 'Living',     label: 'Dusting surfaces',         est: 0.5 },
  { id: 'tidy',          group: 'Living',     label: 'Tidy & declutter',         est: 0.6 },
  { id: 'windows_in',    group: 'Living',     label: 'Windows (inside)',         est: 1.0 },
  { id: 'blinds',        group: 'Living',     label: 'Blinds & shades',          est: 0.5 },
  { id: 'baseboards',    group: 'Living',     label: 'Baseboards',               est: 0.6 },
  { id: 'closets',       group: 'Bedrooms',   label: 'Closet organizing',        est: 1.5 },

  // Laundry
  { id: 'laundry_wash',  group: 'Laundry',    label: 'Wash & dry',               est: 0.5 },
  { id: 'laundry_fold',  group: 'Laundry',    label: 'Fold & put away',          est: 0.8 },
  { id: 'ironing',       group: 'Laundry',    label: 'Ironing',                  est: 1.0 },
  { id: 'delicates',     group: 'Laundry',    label: 'Hand-wash delicates',      est: 0.5 },

  // Occasions & heavy
  { id: 'shabbos',       group: 'Occasions',  label: 'Shabbos prep',             est: 3.0 },
  { id: 'yomtov',        group: 'Occasions',  label: 'Yom Tov prep',             est: 4.0, seasonal: true },
  { id: 'post_party',    group: 'Occasions',  label: 'After a simcha / party',   est: 3.0 },
  { id: 'deep_clean',    group: 'Occasions',  label: 'Full deep clean',          est: 5.0 },
  { id: 'move_out',      group: 'Occasions',  label: 'Move-in / move-out',       est: 6.0 },
  { id: 'post_reno',     group: 'Occasions',  label: 'Post-construction',        est: 6.0 },

  // Extras
  { id: 'pets',          group: 'Extras',     label: 'Pet areas & litter',       est: 0.3 },
  { id: 'basement',      group: 'Extras',     label: 'Basement / playroom',      est: 1.0 },
  { id: 'garage',        group: 'Extras',     label: 'Garage or porch sweep',    est: 0.5 },
  { id: 'meal_prep',     group: 'Extras',     label: 'Light meal prep',          est: 1.0 },
  { id: 'groceries',     group: 'Extras',     label: 'Put away groceries',       est: 0.3 },
  { id: 'kids_tidy',     group: 'Extras',     label: 'Kids rooms & toys',        est: 0.7 },
];

export const TRAITS: Trait[] = [
  { id: 'own_supplies', label: 'Brings own supplies' },
  { id: 'eco',          label: 'Eco-friendly products' },
  { id: 'pet_ok',       label: 'Comfortable with pets' },
  { id: 'has_car',      label: 'Has own car' },
  { id: 'heavy_lift',   label: 'Can do heavy lifting' },
  { id: 'organizer',    label: 'Strong organizer' },
  { id: 'kosher_exp',   label: 'Kosher-home experienced' },
  { id: 'no_sat',       label: 'Does not work Saturday' },
  { id: 'no_sun',       label: 'Does not work Sunday' },
];

export const PAY_METHODS: PayMethod[] = [
  { id: 'cash',   label: 'Cash' },
  { id: 'zelle',  label: 'Zelle' },
  { id: 'venmo',  label: 'Venmo' },
  { id: 'cashapp',label: 'Cash App' },
  { id: 'check',  label: 'Check' },
  { id: 'card',   label: 'Card in app' },
];

/** Deliberately different for each side of the market. */
export const CLEANER_RATING_DIMS: RatingDim[] = [
  { id: 'thorough',  label: 'Thoroughness' },
  { id: 'reliable',  label: 'On time & showed up' },
  { id: 'comm',      label: 'Communication' },
  { id: 'care',      label: 'Care with belongings' },
];
export const HOUSE_RATING_DIMS: RatingDim[] = [
  { id: 'friendly',  label: 'Friendly & respectful' },
  { id: 'clear',     label: 'Clear about the work' },
  { id: 'paid',      label: 'Paid as agreed' },
  { id: 'ready',     label: 'Home was ready to clean' },
];

export const FEEDBACK_TOPICS = [
  { id: 'bug',       label: 'Something is broken' },
  { id: 'confusing', label: 'Something is confusing' },
  { id: 'missing',   label: 'Something is missing' },
  { id: 'person',    label: 'A problem with someone' },
  { id: 'like',      label: 'Something I like' },
];

/** Push needs native apps and nobody reads email. In this market it is
 *  WhatsApp and SMS, so those are the channels we actually build on. */
export const NOTIFY_CHANNELS = [
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'sms',      label: 'Text message' },
  { id: 'both',     label: 'Both' },
  { id: 'app',      label: 'In the app only' },
];
